// atoms/appDataAtom.ts  (keep the path so imports don't change)
import { useEffect, useCallback } from "react";
import { create } from "zustand";
import moment from "moment";
import apiClient from "../apiClient";
import { useAuth0 } from "@auth0/auth0-react";
import { IChangeLog, ICharge, ICorpStation, IMenuApp, ISpecialHandlingCode, Manual } from "../globals/interfaces";
import { useAppContext } from "../context";

interface AppData {
    loaded: boolean;
    loading: boolean;
    error: any;
    stations: Array<ICorpStation>;
    charges: Array<ICharge>;
    menuApps: { system: Array<IMenuApp>; user: Array<IMenuApp> };
    shcs: Array<ISpecialHandlingCode>;
    manuals: Array<Manual>;
    changeLogData: Array<IChangeLog>;
    loggedInUsers: any;
}

const initialData: AppData = {
    loaded: false,
    loading: false,
    error: null,
    stations: [],
    charges: [],
    menuApps: { system: [], user: [] },
    shcs: [],
    manuals: [],
    changeLogData: [],
    loggedInUsers: {},
};

type UserKey = { s_unit: string; s_email: string };

interface AppDataStore {
    data: AppData;
    /** key of the fetch we already have, e.g. "MSP|a@b.com" — null when nothing loaded */
    fetchedKey: string | null;
    /** in-flight request, shared by every caller that arrives while it's pending */
    inFlight: Promise<void> | null;
    fetch: (user: UserKey) => Promise<void>;
    refresh: (user: UserKey) => Promise<void>;
    setData: (next: AppData | ((prev: AppData) => AppData)) => void;
}

const keyOf = (u: UserKey) => `${u.s_unit}|${u.s_email}`;

export const useAppDataStore = create<AppDataStore>((set, get) => ({
    data: initialData,
    fetchedKey: null,
    inFlight: null,

    setData: (next) =>
        set((state) => ({
            data: typeof next === "function" ? (next as (p: AppData) => AppData)(state.data) : next,
        })),

    fetch: async (user) => {
        const key = keyOf(user);
        const { fetchedKey, inFlight } = get();

        if (fetchedKey === key) return;        // already have this unit's data
        if (inFlight) return inFlight;         // someone else is already fetching

        const promise = (async () => {
            set((s) => ({ data: { ...s.data, loading: true, error: null } }));
            try {
                const res = await apiClient.post("/appData", {
                    today: moment().local().format("YYYY-MM-DD"),
                    s_unit: user.s_unit,
                    s_email: user.s_email,
                });
                set({
                    data: { ...res.data, loaded: true, loading: false, error: null },
                    fetchedKey: key,
                });
            } catch (error: any) {
                set((s) => ({
                    data: { ...s.data, loading: false, loaded: true, error: error.message },
                }));
            } finally {
                set({ inFlight: null });
            }
        })();

        set({ inFlight: promise });
        return promise;
    },

    refresh: async (user) => {
        set({ fetchedKey: null });
        return get().fetch(user);
    },
}));

export default function useAppData() {
    const { user } = useAppContext();
    const { isAuthenticated } = useAuth0();

    const data = useAppDataStore((s) => s.data);
    const fetch = useAppDataStore((s) => s.fetch);
    const refresh = useAppDataStore((s) => s.refresh);
    const setAppData = useAppDataStore((s) => s.setData);

    useEffect(() => {
        if (!isAuthenticated || !user?.s_email || !user?.s_unit) return;
        fetch({ s_unit: user.s_unit, s_email: user.s_email });
    }, [isAuthenticated, user?.s_unit, user?.s_email, fetch]);

    const refreshAppData = useCallback(
        () => refresh({ s_unit: user.s_unit, s_email: user.s_email }),
        [user?.s_unit, user?.s_email, refresh]
    );

    return {
        appDataLoaded: data.loaded,
        loading: data.loading,
        error: data.error,
        stations: data.stations,
        charges: data.charges,
        menuApps: data.menuApps,
        shcs: data.shcs,
        manuals: data.manuals,
        changeLogData: data.changeLogData,
        loggedInUsers: data.loggedInUsers,
        setAppData,
        refreshAppData,
    };
}